import { useState, useEffect } from 'react';
import { Box, Collapse, Alert, AlertTitle } from '@mui/material';
import { ErrorMessage } from '../ErrorMessage';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, AppState, AppStore } from '../../store';
import { GenericSimpleForm } from '../../components/Layouts/GenericSimpleForm';
import { FeatureFlagFormFields, EAction, EActionType, EDataTableType } from '../../data/Constants';
import { useTranslation } from 'react-i18next';
import { getApplicationDetails } from '../../store/states/ApplicationSlice';
import { setAlertMessage } from '../../store/states/AlertMessageSlice';
import { resetDialogMessage } from '../../store/states/DialogSlice';
import { resetError } from '../../store/states/ErrorSlice';
import {
  createFeatureFlag,
  getFeatureFlagDetails,
  getFeatureFlags,
  updateFeatureFlag,
} from '../../store/states/FeatureFlagSlice';
import { TFunction } from 'i18next';

import type {
  IFeatureFlag,
  IError,
  IManipulationProps,
  IApplication,
  IAPIResponse,
} from '../../../types';

/**
 * Calls the appropriate API (create or update) to manage feature flags
 * @param {string} manipulationType - The type of manipulation (EActionType.Edit or EActionType.Create)
 * @param {IManipulationProps} props - Props containing token and other manipulation data
 * @param {IFeatureFlag} formData - The feature flag data to be created or updated
 * @param {function} t - Translation function for internationalization
 * @param {AppDispatch} dispatch - Redux dispatch function
 * @returns {void}
 */
function callCreateOrUpdateApi(
  manipulationType: string,
  formData: IFeatureFlag,
  t: TFunction,
  dispatch: AppDispatch
) {
  if (manipulationType === EActionType.Edit) {
    dispatch(
      updateFeatureFlag({
        request: {
          ...formData,
          page: formData.page.trim(),
          flagName: formData.flagName.trimEnd().toUpperCase(),
          description: formData.description?.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.update'),
        t('messages.success.update'),
        dispatch
      );
    });
  } else {
    dispatch(
      createFeatureFlag({
        request: {
          ...formData,
          page: formData.page.trim(),
          flagName: formData.flagName.trimEnd().toUpperCase(),
          description: formData.description?.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        dispatch
      );
    });
  }
}

/**
 * Handles API response after create or update operations
 * Updates feature flags list and displays appropriate success/error messages
 * @param {IManipulationProps} props - Props containing token and other manipulation data
 * @param {string} failMessage - Message to display on failure
 * @param {string} successMessage - Message to display on success
 * @param {AppDispatch} dispatch - Redux dispatch function
 * @returns {void}
 */
function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
) {
  const featureFlags = AppStore.getState().featureFlags.featureFlags;
  if (res?.data) {
    dispatch(
      getFeatureFlags({
        page: Number(featureFlags.currentPage),
        limit: featureFlags.recordsPerPage,
        searchCode: featureFlags.searchKey,
        activeOnly: false,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  } else {
    const message = res?.error?.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
}

/**
 * Fetches application details from API and updates the form fields
 * Filters applications to show only active ones in the dropdown
 * @param {IManipulationProps} props - Props containing authentication token
 * @param {function} setApplicationsAvailable - State setter for applications list
 * @param {AppDispatch} dispatch - Redux dispatch function for API calls
 * @returns {Promise<void>} Promise that resolves when applications are loaded
 */
const setApplicationDetails = async (
  setApplicationsAvailable: (value: React.SetStateAction<IApplication[]>) => void,
  dispatch: AppDispatch
) => {
  let applications: IApplication[] = [];
  await dispatch(getApplicationDetails({ request: [], isActive: true })).then((res) => {
    const payload = res?.payload as { data?: IApplication[] };
    if (payload?.data) {
      applications = payload.data;
    }
    FeatureFlagFormFields.filter((app) => app.field == 'applicationName')?.map(
      (app) => (app.values = applications)
    );
  });
  setApplicationsAvailable(applications);
};

/**
 * Loads existing feature flag data for editing
 * Modifies form fields based on the feature flag's active status
 * @param {IManipulationProps} props - Props containing authentication token and feature flag ID
 * @param {function} setInitialFeatureFlagData - State setter for initial form data
 * @param {AppDispatch} dispatch - Redux dispatch function for API calls
 * @returns {Promise<void>} Promise that resolves when feature flag data is loaded
 */
const loadFormData = async (
  props: IManipulationProps,
  setInitialFeatureFlagData: (data: IFeatureFlag) => void,
  dispatch: AppDispatch
) => {
  let featureFlagDetailsResponse = await dispatch(
    getFeatureFlagDetails({ request: [props.id] })
  ).unwrap();

  if (!('data' in featureFlagDetailsResponse) || !featureFlagDetailsResponse.data) {
    return;
  }
  const featureFlagDetails = featureFlagDetailsResponse.data[0];
  const actions = FeatureFlagFormFields.find((app) => app.field == 'actions');
  if (featureFlagDetails.isActive) {
    actions.values = actions.values?.filter((item) => item.code !== 'isActive');
  } else {
    // Check if "isActive" is already in the array
    const exists = actions.values?.some((item) => item.code === 'isActive');
    if (!exists && actions.values) {
      actions.values.push({ code: 'isActive', name: 'isActive' });
    }
  }
  setInitialFeatureFlagData(featureFlagDetails);
};

export const FeatureFlagsManipulationForm = (props: IManipulationProps) => {
  const manipulationType = props.type; //To get the type of manipulation being performed
  const { t } = useTranslation();
  const [displayAlertMessage, setDisplayAlertMessage] = useState<boolean>(false);
  const [applicationsAvailable, setApplicationsAvailable] = useState<IApplication[]>([]);
  const error = useSelector((state: AppState) => state.error);
  const dispatch = useDispatch<AppDispatch>();
  const initialData: IFeatureFlag = {
    flagName: '',
    description: '',
    applicationName: '',
    page: '',
    isEditable: true,
    isVisible: true,
    isActive: true,
  };
  const [initialFormData, setInitialFormData] = useState<IFeatureFlag>(initialData);

  const handleError = (error: IError, setDisplayAlertMessage: (show: boolean) => void) => {
    if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
      setDisplayAlertMessage(true);
    }
  };

  useEffect(() => {
    setApplicationDetails(setApplicationsAvailable, dispatch);
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, setInitialFormData, dispatch);
    }
  }, []);

  useEffect(() => {
    handleError(error, setDisplayAlertMessage);
  }, [error]);

  useEffect(() => {
    FeatureFlagFormFields.filter((app) => app.field == 'applicationName')?.map(
      (app) => (app.values = applicationsAvailable.filter((app) => app.isActive))
    );
  }, [applicationsAvailable]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
  };
  const onSubmit = (data: IFeatureFlag) => {
    dispatch(resetError());
    setDisplayAlertMessage(false);
    callCreateOrUpdateApi(manipulationType, data, t, dispatch);
  };

  return (
    <Box className="manipulationBox pt-1">
      <Box className="manipulationForm">
        <Collapse in={displayAlertMessage} className="show-alert">
          <Alert className="errorMessage" severity="error" onClose={handleClose}>
            <AlertTitle>
              <ErrorMessage error={error} manipulationType={manipulationType} />
            </AlertTitle>
          </Alert>
        </Collapse>
        <GenericSimpleForm
          formFields={FeatureFlagFormFields}
          initialData={initialFormData}
          onSubmit={onSubmit}
          manipulationType={manipulationType}
          setDisplayAlertMessage={setDisplayAlertMessage}
          type={EDataTableType.FeatureFlags}
          uniqueKey="flagName"
        />
      </Box>
    </Box>
  );
};
